import {
    ApprovalScopeEnum,
    DocumentTypeEnum,
    Expense,
    ExpenseKindEnum,
    ExpenseRequestTypeEnum,
    ExpenseStatusEnum,
    Hospitality,
    type InboxInvoice,
    Mileage,
    PerDiem,
} from "@finway-group/shared/lib/models"
import * as Sentry from "@sentry/react"
import { Form } from "antd"
import { type Store } from "antd/lib/form/interface"
import moment from "moment"
import React, { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { parseCurrencyInput } from "~Components/currencyInput/config"
import { InvoiceRequestForm } from "~Components/forms/expenseForm/InvoiceRequestForm"
import { PurchaseRequestForm } from "~Components/forms/expenseForm/PurchaseRequestForm"
import { ReimbursementRequestForm } from "~Components/forms/expenseForm/ReimbursementRequestForm"
import { TripFolderRequestForm } from "~Components/forms/expenseForm/TripFolderRequestForm"
import { useExpenseFormContext } from "~Components/modals/expenseCreateForm.context"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useSubCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useEmployees } from "~Shared/hooks/employee.hooks"
import { fillFormWithOcrData } from "~Shared/hooks/ocr.hooks"
import { useTaxes } from "~Shared/hooks/tax.hooks"
import { useExpenseOCRPOMatching } from "~Shared/hooks/useExpenseOCRPOMatching"
import { useLoggedInEmployeeProfile } from "~Shared/hooks/user.hooks"
import { useEmployee } from "~Shared/queries/employee.queries"
import { fetchVendor, useVendor } from "~Shared/queries/vendors/vendor.queries"
import { ApprovalProcessService, CostCenterService, ExpenseHttpService } from "~Shared/services"
import { determineApprovalScopes, getNextApproverId, getNextApproverORFirstNotSkippedApprover, getResetApprovalProcessesCopy } from "~Shared/utils/approvalProcess.utils"
import {
    buildExpenseAccountingRulePatch,
    buildUpdateDataAccordingToSetting,
    getPerformanceDateFieldsToValidate,
    initializeExpenseItemSplitsForForm,
    PERFORMANCE_DATE_FORM_FIELDS,
    type PerformanceDateFormValues,
    sanitizeInvoiceDueDate,
    syncExpenseSplits,
} from "~Shared/utils/expense.utils"
import { insertIf, isEmptyObject, isNotSet } from "~Shared/utils/helper.utils"
import { useChangeEffect } from "~Shared/utils/hooks/useChangeEffect"
import { useUpdateEffect } from "~Shared/utils/hooks/useUpdateEffect"
import { SubscriptionRequestForm } from "./SubscriptionRequestForm"

interface ExpenseFormProps {
    onSubmit: (expense: Expense, extraData?: { originalApproverId?: string }) => void
    onFormValuesChange: () => void
    inboxOcrSource?: InboxInvoice
}

export const ExpenseForm = ({ onSubmit, inboxOcrSource, onFormValuesChange: onFormValuesChangeProp }: ExpenseFormProps) => {
    const { t } = useTranslation()
    const loggedInProfile = useLoggedInEmployeeProfile()
    const employees = useEmployees({ excludeDeleted: true, includeAutoApprover: true })
    const costCenters = useSubCostCenters(true)
    const taxes = useTaxes()
    const company = useCompany()

    // prettier-ignore
    const [
        {
            expense,
            isNew,
            expenseForm,
            type,
            inboxInvoices,
            updateExpense,
            ocrSettings,
            isInvoiceRequest,
            isPreApprovedSubscription,
            setPrefilledVendorData,
            setIsOcrProcessed,
            setActiveDocumentsTab,
            folder,
            setIsBuildingApprovalProcesses,
            setIsFormTouched,
        },
        _setCtx,
        { selectedPurchaseOrders, setSelectedPurchaseOrders },
    ] = useExpenseFormContext()
    // add preset values, even if they were deleted. Otherwise it'll show the ID only.
    const { updatePOByOCR } = useExpenseOCRPOMatching(expense, ocrSettings, setSelectedPurchaseOrders)
    // approver at the moment of modal opening, to check if it has changed or not
    const { data: originalNextApprover } = useEmployee(getNextApproverId(expense.approvalProcesses))
    const originalApproverIdRef = useRef(!originalNextApprover?.activeCompanyProfile?.deleted ? originalNextApprover?.id : undefined)

    if (expense.costCenter && !costCenters.find((cc) => cc._id === expense.costCenter)) {
        costCenters.push(CostCenterService.getCostCenterById(expense.costCenter))
    }

    const { data: vendor } = useVendor(expense?.vendor?._id)
    const paymentTerm = vendor?.rule?.paymentTerm

    const updateDueDateBasedOnRule = (invoiceDate: any) => {
        if (paymentTerm && invoiceDate) {
            return { invoiceDueDate: moment(invoiceDate).add(paymentTerm, "days") }
        }
        return {}
    }

    // the values the form was last filled with, to tell an OCR fill apart from an unrelated expense update
    const performanceDateValues = useRef<PerformanceDateFormValues>(expense)

    // New expenses that belong to a folder will follow its folder approval process.
    useEffect(() => {
        if (folder && isNew) {
            expense.approvalProcesses = getResetApprovalProcessesCopy(folder.approvalProcesses)
        }
    }, [])

    useEffect(() => {
        const initExpense = { ...expense }
        const initSplits = initializeExpenseItemSplitsForForm(initExpense)
        // read before the write, which is what drops the error state
        const erroredPerformanceDateFields = PERFORMANCE_DATE_FORM_FIELDS.filter((field) => expenseForm.getFieldError(field).length > 0)
        // Note: Antd has a bug where controlled fields (value prop) of Select does not work, so we need to update the form instance.
        const nextApproverId = getNextApproverORFirstNotSkippedApprover(expense.approvalProcesses)
        expenseForm.setFieldsValue({ ...expenseForm.getFieldsValue(), ...initExpense, splits: initSplits, nextApprover: nextApproverId })

        // OCR fills these two through the expense state, so their rules only run again on submit. Validating them here,
        // right after the values reached the form, is what puts the hard limit error on the field straight away.
        const performanceDateFieldsToValidate = getPerformanceDateFieldsToValidate(performanceDateValues.current, initExpense, erroredPerformanceDateFields)
        performanceDateValues.current = initExpense
        if (performanceDateFieldsToValidate.length) {
            // a rejection means a rule rejected, which is what should show on the field
            expenseForm.validateFields(performanceDateFieldsToValidate).catch(() => undefined)
        }
    }, [expense])

    useEffect(() => {
        const fillFormOcrData = async () => {
            if (inboxInvoices?.length && loggedInProfile.settings.enableOcr && inboxOcrSource) {
                const { ocrResults } = inboxOcrSource
                if (!isNotSet(ocrResults) && !isEmptyObject(ocrResults)) {
                    try {
                        await fillFormWithOcrData({
                            formInstance: expenseForm,
                            expense,
                            ocrObject: ocrResults,
                            taxes,
                            updateExpense,
                            setPrefilledVendorData,
                            t,
                            ocrItemizationEnabled: ocrSettings.isOcrItemizationEnabled,
                        })
                    } finally {
                        setIsOcrProcessed(true)
                    }
                }
                updatePOByOCR(ocrResults)
            }
        }
        fillFormOcrData()
    }, [inboxInvoices?.length])

    // Initialize approval processes on new forms.
    useEffect(() => {
        if (!isNew || expense.folderId || folder) {
            return
        }
        const buildApprovalProcesses = async () => {
            try {
                setIsBuildingApprovalProcesses(true)
                const scopes = [...insertIf(expense.status === ExpenseStatusEnum.PURCHASE_PENDING, ApprovalScopeEnum.EXPENSE_PURCHASE), ApprovalScopeEnum.EXPENSE_INVOICE]
                const approvalProcesses = await ExpenseHttpService.buildApprovalProcesses(scopes, expense)
                updateExpense({ approvalProcesses })
            } catch (buildApprovalProcessesError: unknown) {
                Sentry.captureException(buildApprovalProcessesError, { extra: { fun: "buildApprovalProcesses" } })
            } finally {
                setIsBuildingApprovalProcesses(false)
            }
        }
        buildApprovalProcesses()
    }, [])

    // Update expense approval processes on change
    // prettier-ignore

    // only changes if the cost center or cost center 2 of a split changes
    const splitsCostAllocationKey = useMemo(
        () =>
            expense.splits
                ?.map((s, index) => {
                    const cc1 = s.costCenter ?? ""
                    const cc2 = s.costCenter2 ?? ""

                    return cc1 || cc2 ? `${index}:${cc1}|${cc2}` : null
                })
                .filter(Boolean)
                .join(",") ?? "",
        [expense.splits],
    )

    useChangeEffect(
        (changedDeps: any) => {
            const evaluateExpenseSideEffect = async () => {
                let currentVendor = vendor
                if (!currentVendor && expense.vendor?._id) {
                    // BUG-10586 - Vendor is needed in this effect, but sometimes useVendor doesnt have the cached data yet.
                    // In here we have to wait until the vendor is actually fetched into the react-query cache so that the calculation here is accurate.
                    currentVendor = await fetchVendor(expense.vendor?._id)
                }
                let updateData: any

                // Only update the expense data if the vendor or requestedBy has changed
                if (changedDeps.vendor || changedDeps.requestedBy) {
                    updateData = {
                        ...buildUpdateDataAccordingToSetting(expense, employees, company, currentVendor),
                        ...(updateDueDateBasedOnRule(expense.invoiceDate) as any),
                    }
                }

                const allocationPatch = buildExpenseAccountingRulePatch(expense, updateData ?? {})
                const mergedUpdate = { ...(updateData ?? {}), ...allocationPatch }

                // Expenses inside a folder do not change the approval process. They follow the folder approval process.
                // The expense's approval process has been determined in the useEffect with [] dependency
                if (expense.folderId || folder) {
                    if (Object.keys(mergedUpdate).length > 0) {
                        updateExpense(mergedUpdate)
                    }

                    return
                }

                const updatedExpense = new Expense({ ...expense, ...mergedUpdate })

                // When no scope needs a rebuild, the approval processes captured in this closure are still the ones from
                // before a concurrent manual next-approver selection, so writing them back would revert that selection.
                if (!determineApprovalScopes(updatedExpense, company, { valuesChange: changedDeps, isPreApprovedSubscription }).length) {
                    if (Object.keys(mergedUpdate).length > 0) {
                        updateExpense(mergedUpdate)
                    }

                    return
                }

                try {
                    setIsBuildingApprovalProcesses(true)
                    const approvalProcesses = await ApprovalProcessService.getApprovalProcessesAfterExpenseUpdate(updatedExpense, company, {
                        valuesChange: changedDeps,
                        isPreApprovedSubscription,
                    })

                    if (!approvalProcesses) {
                        return
                    }
                    updateExpense({ ...mergedUpdate, approvalProcesses })
                } catch (updateApprovalProcessError: unknown) {
                    Sentry.captureException(updateApprovalProcessError, { extra: { fun: "updateApprovalProcess" } })
                } finally {
                    setIsBuildingApprovalProcesses(false)
                }
            }
            evaluateExpenseSideEffect()
        },
        [
            expense.requestedBy?._id,
            expense.costCenter,
            expense.costCenter2,
            expense.vendor?._id,
            expense.totalNetPrice,
            expense.totalGrossPrice,
            expense.totalTaxPrice,
            expense.taxRate,
            expense.paymentFlowInformation,
            expense.paymentOption,
            expense.kind,
            splitsCostAllocationKey,
        ],
        [
            "requestedBy",
            "costCenter",
            "costCenter2",
            "vendor",
            "totalNetPrice",
            "totalGrossPrice",
            "totalTaxPrice",
            "taxRate",
            "paymentFlowInformation",
            "paymentOption",
            "kind",
            "splitsCostAllocation",
        ],
        useUpdateEffect,
    )

    useUpdateEffect(() => {
        updateExpense(updateDueDateBasedOnRule(expense.invoiceDate) as any)
    }, [expense.invoiceDate])

    const onFinish = (form: Store) => {
        form.totalNetPrice = parseCurrencyInput(form.totalNetPrice)
        form.totalTaxPrice = parseCurrencyInput(form.totalTaxPrice)

        form = syncExpenseSplits(form, expense.splitType)

        form.performancePeriodStartDate = form.performancePeriod?.[0]
        form.performancePeriodEndDate = form.performancePeriod?.[1]
        delete form.performancePeriod

        const offers = expenseForm.getFieldValue("offers")
        if (offers) {
            form.offers = !isInvoiceRequest ? offers.map((offer: any) => offer.response || offer) : [...offers]
        }

        // Transforming form data to be compatible with expense
        if (expense.kind === ExpenseKindEnum.HOSPITALITY) {
            form.attendees = form.attendees?.map(({ value, label }: any) => ({ internal: label !== undefined && value !== label, user: value }))
        }

        const formData: Record<string, unknown> = {
            ...expense,
            ...form,
        }
        formData.invoiceDueDate = sanitizeInvoiceDueDate(formData)

        const originalApproverId = originalApproverIdRef.current
        const extraSubmitData =
            originalApproverId || selectedPurchaseOrders.length > 0
                ? {
                      ...(originalApproverId && { originalApproverId }),
                  }
                : undefined
        switch (form.kind) {
            case ExpenseKindEnum.HOSPITALITY:
                onSubmit(new Hospitality(formData), extraSubmitData)
                break
            case ExpenseKindEnum.MILEAGE:
                onSubmit(new Mileage(formData), extraSubmitData)
                break
            case ExpenseKindEnum.PER_DIEM:
                onSubmit(new PerDiem(formData), extraSubmitData)
                break
            default:
                onSubmit(new Expense(formData), extraSubmitData)
        }
    }

    const onFormFinishFailed = ({ errorFields }: any) => {
        if (isInvoiceRequest) {
            expenseForm.scrollToField(["invoiceNumber"])
            if (errorFields.some((errorField: any) => errorField.name.includes("invoices"))) {
                setActiveDocumentsTab(DocumentTypeEnum.INVOICE)
            }
        } else {
            expenseForm.scrollToField(errorFields[0].name)
        }
    }

    const onFormValuesChange = () => {
        setIsFormTouched(true)
        onFormValuesChangeProp()
    }

    return (
        <Form
            form={expenseForm}
            layout="vertical"
            name="CreateExpenseForm"
            onFinish={onFinish}
            onFinishFailed={onFormFinishFailed}
            autoComplete="off"
            // Use onValuesChange prop from antd Form component
            // setFieldsValue do not trigger onValuesChange
            // only user interactive can trigger the change event Reference
            // See: https://4x.ant.design/components/form/#setFieldsValue-do-not-trigger-onFieldsChange-or-onValuesChange
            onValuesChange={onFormValuesChange}
        >
            {type === ExpenseRequestTypeEnum.PURCHASE && <PurchaseRequestForm />}

            {type === ExpenseRequestTypeEnum.INVOICE && <InvoiceRequestForm />}
            {type === ExpenseRequestTypeEnum.SUBSCRIPTION && <SubscriptionRequestForm />}
            {type === ExpenseRequestTypeEnum.REIMBURSEMENT && <ReimbursementRequestForm />}
            {type === ExpenseRequestTypeEnum.TRIP_FOLDER && <TripFolderRequestForm />}
        </Form>
    )
}
